import { supabase } from '../configs/supabase';

/**
 * Get user's subscription from Supabase
 * @param {string} userId - User ID
 * @returns {Promise<{subscription: object, error: object}>}
 */
export const getUserSubscription = async (userId) => {
  try {
    if (!userId) {
      return { subscription: null, error: null };
    }

    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') {
      // PGRST116 means no rows found, which is fine
      console.error('Error fetching subscription:', error);
      return { subscription: null, error };
    }

    return { subscription: data || null, error: null };
  } catch (error) {
    console.error('Exception fetching subscription:', error);
    return { subscription: null, error };
  }
};

/**
 * Get or create free subscription for a user
 * @param {string} userId - User ID
 * @returns {Promise<{subscription: object, error: object}>}
 */
export const getOrCreateFreeSubscription = async (userId) => {
  try {
    if (!userId) {
      return { subscription: null, error: null };
    }

    // First try to get existing subscription
    const { subscription, error: fetchError } = await getUserSubscription(userId);

    if (subscription) {
      return { subscription, error: null };
    }

    if (fetchError && fetchError.code !== 'PGRST116') {
      return { subscription: null, error: fetchError };
    }

    // Create free subscription using the database function
    const { data, error } = await supabase.rpc('get_or_create_free_subscription', {
      p_user_id: userId,
    });

    if (error) {
      console.error('Error creating free subscription:', error);
      return { subscription: null, error };
    }

    return { subscription: data, error: null };
  } catch (error) {
    console.error('Exception creating free subscription:', error);
    return { subscription: null, error };
  }
};

/**
 * Get current usage for a user or anonymous user
 * @param {string} userId - User ID (optional for anonymous users)
 * @param {string} anonymousId - Anonymous ID (IP + fingerprint hash)
 * @returns {Promise<{usage: object, error: object}>}
 */
export const getCurrentUsage = async (userId = null, anonymousId = null) => {
  try {
    const now = new Date().toISOString();

    let query = supabase
      .from('usage_tracking')
      .select('*')
      .gte('period_end', now)
      .order('period_start', { ascending: false })
      .limit(1);

    if (userId) {
      query = query.eq('user_id', userId).is('anonymous_id', null);
    } else if (anonymousId) {
      query = query.eq('anonymous_id', anonymousId).is('user_id', null);
    } else {
      return { usage: null, error: { message: 'Either userId or anonymousId required' } };
    }

    const { data, error } = await query.single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching usage:', error);
      return { usage: null, error };
    }

    return { usage: data || null, error: null };
  } catch (error) {
    console.error('Exception fetching usage:', error);
    return { usage: null, error };
  }
};

/**
 * Record analysis usage
 * @param {string} userId - User ID (optional)
 * @param {string} anonymousId - Anonymous ID (optional)
 * @returns {Promise<{success: boolean, error: object}>}
 */
export const recordUsage = async (userId = null, anonymousId = null) => {
  try {
    const now = new Date();
    const periodEnd = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now

    // Get current usage
    const { usage: currentUsage } = await getCurrentUsage(userId, anonymousId);

    if (currentUsage) {
      // Update existing usage record
      const { error } = await supabase
        .from('usage_tracking')
        .update({
          analysis_count: currentUsage.analysis_count + 1,
          updated_at: now.toISOString(),
        })
        .eq('id', currentUsage.id);

      if (error) {
        console.error('Error updating usage:', error);
        return { success: false, error };
      }
    } else {
      // Create new usage record
      const usageData = {
        analysis_count: 1,
        period_start: now.toISOString(),
        period_end: periodEnd.toISOString(),
      };

      if (userId) {
        usageData.user_id = userId;
      } else if (anonymousId) {
        usageData.anonymous_id = anonymousId;
      } else {
        return { success: false, error: { message: 'Either userId or anonymousId required' } };
      }

      const { error } = await supabase
        .from('usage_tracking')
        .insert(usageData);

      if (error) {
        console.error('Error creating usage record:', error);
        return { success: false, error };
      }
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Exception recording usage:', error);
    return { success: false, error };
  }
};

/**
 * Check if user can perform analysis based on subscription and usage
 * @param {object} subscription - Subscription object
 * @param {object} usage - Usage tracking object
 * @returns {Promise<{canAnalyze: boolean, reason?: string, remaining?: number, limit?: number}>}
 */
export const checkAnalysisLimit = async (subscription, usage) => {
  try {
    const planType = subscription?.plan_type || 'free';
    const status = subscription?.status || 'active';

    // Check if subscription is active
    if (subscription && status !== 'active' && status !== 'trialing') {
      return {
        canAnalyze: false,
        reason: 'subscription_inactive',
        message: 'Your subscription is not active. Please update your payment method.',
      };
    }

    // Check usage limits based on plan
    if (planType === 'plan_c') {
      // Unlimited plan
      return { canAnalyze: true, reason: 'unlimited_plan', limit: -1 };
    }

    if (planType === 'plan_b') {
      // Basic: 5 analyses per 24 hours
      const currentCount = usage?.analysis_count || 0;
      const limit = 5;

      if (currentCount >= limit) {
        return {
          canAnalyze: false,
          reason: 'limit_reached',
          remaining: 0,
          limit,
          message: `You've reached your daily limit of ${limit} analyses. Upgrade to unlimited for more.`,
        };
      }

      return {
        canAnalyze: true,
        reason: 'within_limit',
        remaining: limit - currentCount,
        limit,
      };
    }

    // Free tier: 1 analysis per 24 hours
    const currentCount = usage?.analysis_count || 0;
    const limit = 1;

    if (currentCount >= limit) {
      const periodEnd = usage?.period_end ? new Date(usage.period_end) : null;
      const now = new Date();
      const timeUntilReset = periodEnd ? Math.max(0, Math.ceil((periodEnd - now) / (1000 * 60))) : null;

      return {
        canAnalyze: false,
        reason: 'limit_reached',
        remaining: 0,
        limit,
        timeUntilReset,
        message: 'You\'ve used your free analysis. Subscribe to a plan for more analyses.',
      };
    }

    return {
      canAnalyze: true,
      reason: 'within_limit',
      remaining: limit - currentCount,
      limit,
    };
  } catch (error) {
    console.error('Error checking analysis limit:', error);
    // Fail open - allow analysis if check fails
    return { canAnalyze: true, reason: 'check_failed' };
  }
};
